import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { defer, forkJoin } from 'rxjs';
import { publicMethods, baseUrl } from '../globals';
import { AuthenticationService } from './authentication.service';
import {
  CombinedQuotaBalance,
  MAGIC_INSIGHTS_CREDITS_FEATURE_NAME,
} from '../classes/magic-insights-credits';

@Injectable()
export class QuotaManagementService {
  constructor(
    private http: HttpClient,
    private pubMethods: publicMethods,
    private auth: AuthenticationService
  ) {}

  isLoading = {
    loadingQuotaDetails: false,
    loadingTeamQuotaDetails: false,
    loadingUserQuotaDetails: false,
    settingQuota: false,
  };

  getOrganisationQuotaDetails(
    featureName: string,
    quotaType: string
  ): Observable<any> {
    let params = new HttpParams()
      .set('featureName', featureName)
      .set('quotaType', quotaType);
    this.isLoading['loadingQuotaDetails'] = true;
    return this.http
      .post(
        baseUrl + '/return_organisation_quota_details/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['loadingQuotaDetails'] = false)),
        catchError(
          this.pubMethods.handleError('getOrganisationQuotaDetails', [])
        )
      );
  }

  getTeamQuotaDetails(
    featureName: string,
    quotaType: string,
    teamId: number | string
  ): Observable<any> {
    if (!teamId) {
      teamId = '';
    }
    let params = new HttpParams()
      .set('featureName', featureName)
      .set('quotaType', quotaType)
      .set('teamId', teamId);
    this.isLoading['loadingTeamQuotaDetails'] = true;
    return this.http
      .post(
        baseUrl + '/return_team_quota_details/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['loadingTeamQuotaDetails'] = false)),
        catchError(this.pubMethods.handleError('getTeamQuotaDetails', []))
      );
  }

  getUserQuotaDetails(
    featureName: string,
    quotaType: string,
    manageLoading = true
  ): Observable<any> {
    let params = new HttpParams()
      .set('featureName', featureName)
      .set('quotaType', quotaType);
    if (manageLoading) {
      this.isLoading['loadingUserQuotaDetails'] = true;
    }
    const request = this.http
      .post(
        baseUrl + '/return_user_quota_details/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        catchError(this.pubMethods.handleError('getUserQuotaDetails', []))
      );

    return manageLoading
      ? request.pipe(
          finalize(() => (this.isLoading['loadingUserQuotaDetails'] = false))
        )
      : request;
  }

  getCombinedUserQuotaBalance(
    featureName: string
  ): Observable<CombinedQuotaBalance> {
    return defer(() => {
      this.isLoading['loadingUserQuotaDetails'] = true;
      return forkJoin({
        monthly: this.getUserQuotaDetails(featureName, 'MONTHLY', false),
        bulk: this.getUserQuotaDetails(featureName, 'BULK', false),
      }).pipe(
        map(({ monthly, bulk }) => {
          if (Array.isArray(monthly) || Array.isArray(bulk)) {
            throw new Error('Unable to load the complete quota balance.');
          }

          const assigned =
            Number(monthly?.quota || 0) + Number(bulk?.quota || 0);
          const usage =
            Number(monthly?.usage || 0) + Number(bulk?.usage || 0);

          return {
            assigned,
            usage,
            remaining: assigned - usage,
          };
        }),
        finalize(() => (this.isLoading['loadingUserQuotaDetails'] = false))
      );
    });
  }

  getCombinedMagicInsightsCreditsBalance(): Observable<CombinedQuotaBalance> {
    return this.getCombinedUserQuotaBalance(
      MAGIC_INSIGHTS_CREDITS_FEATURE_NAME
    );
  }

  setQuota(
    featureName: string,
    quotaType: string,
    quota: number,
    userId?: number,
    teamId?: number
  ): Observable<any> {
    let params = new HttpParams()
      .set('featureName', featureName)
      .set('quotaType', quotaType)
      .set('quota', quota.toString());

    if (userId) {
      params = params.set('userId', userId.toString());
    }
    if (teamId) {
      params = params.set('teamId', teamId.toString());
    }

    this.isLoading['settingQuota'] = true;
    return this.http
      .post(
        baseUrl + '/set_quota/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['settingQuota'] = false)),
        catchError(this.pubMethods.handleError('setQuota', []))
      );
  }

  equallyDistributeQuotas(
    featureName: string,
    quotaType: string
  ): Observable<any> {
    let params = new HttpParams()
      .set('featureName', featureName)
      .set('quotaType', quotaType);
    this.isLoading['settingQuota'] = true;
    return this.http
      .post(
        baseUrl + '/equally_distribute_quotas_to_all_users/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['settingQuota'] = false)),
        catchError(this.pubMethods.handleError('equallyDistributeQuotas', []))
      );
  }
}
